/**
 * BlocksBuilder — palette + flat token canvas for the visual formula editor
 * For Media Devoted | Created By Ciro Autiero
 *
 * Single-AST controlled component: parents pass `ast` and an `onChange` that
 * receives the next AST (or `null` while the chip sequence is incomplete).
 * Pass `subformulas` only when this builder edits the master formula so the
 * palette and canvas resolve subref tokens to their display name.
 */

import { FC, useCallback, useEffect, useRef, useState } from 'react';
import type { FormulaBlockNode, FormulaSubformula } from '@trafficpulse/shared';
import { parseExpressionToBlock } from '@trafficpulse/shared';

import { FormulaCanvas } from './canvas';
import BlocksPalette from './BlocksPalette';
import { ChipToken, chipsToText, textToChips } from './blocks.tokens';
import { serializeBlocks } from './blocks.serialize';

export interface BlocksBuilderProps {
  ast: FormulaBlockNode | null;
  onChange: (next: FormulaBlockNode | null) => void;
  /** When provided, palette shows a Subformulas section and chips render names. */
  subformulas?: FormulaSubformula[];
}

const BlocksBuilder: FC<BlocksBuilderProps> = ({ ast, onChange, subformulas }) => {
  const [chips, setChips] = useState<ChipToken[]>(() => textToChips(serializeBlocks(ast)));
  const lastTextRef = useRef<string>(chipsToText(chips));
  /** When true, parent echoes `ast: null` for our in-progress tokens; do not replace chips with []. */
  const suppressEmptyAstSyncRef = useRef(false);
  /**
   * Latest chips + onChange held in refs so the palette/canvas callbacks stay
   * referentially stable. That lets the memoized children skip re-rendering
   * when an edit elsewhere (e.g. another subformula's title) re-renders this
   * builder, while still acting on the current chips and onChange.
   */
  const chipsRef = useRef(chips);
  chipsRef.current = chips;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const incoming = serializeBlocks(ast);
    if (incoming === lastTextRef.current) return;
    if (incoming === '' && ast === null && suppressEmptyAstSyncRef.current) return;
    suppressEmptyAstSyncRef.current = false;
    lastTextRef.current = incoming;
    setChips(textToChips(incoming));
  }, [ast]);

  const apply = useCallback((next: ChipToken[]) => {
    setChips(next);
    const text = chipsToText(next);
    lastTextRef.current = text;
    const parsed = parseExpressionToBlock(text);
    suppressEmptyAstSyncRef.current = parsed === null && text.trim() !== '';
    onChangeRef.current(parsed);
  }, []);

  const handleInsert = useCallback(
    (added: ChipToken[]) => apply([...chipsRef.current, ...added]),
    [apply],
  );
  const handleRemove = useCallback(
    (id: string) => apply(chipsRef.current.filter((c) => c.id !== id)),
    [apply],
  );
  const handleClear = useCallback(() => apply([]), [apply]);
  const handleReorder = useCallback((next: ChipToken[]) => apply(next), [apply]);

  return (
    <div className="space-y-3">
      <BlocksPalette subformulas={subformulas} onInsert={handleInsert} />
      <FormulaCanvas
        chips={chips}
        subformulas={subformulas}
        onRemove={handleRemove}
        onClear={handleClear}
        onReorder={handleReorder}
      />
    </div>
  );
};

export default BlocksBuilder;
